import React, { useEffect, useMemo, useState } from 'react';
import { Input, Modal, ModalBody, ModalHeader } from 'reactstrap';
import SearchableSelect from '@components/atom/SearchableSelect';
import { getSeatList, merchantId_api } from '@lib/api';
import { getSeatCategory, patchBackofficeSeatMove } from '@lib/api/v2/seat';
import useHandleAPICall from '@lib/hooks/useHandleAPICall';
import useHandleError from '@lib/hooks/useHandleError';
import theme from '@theme/index';
import Swal from 'sweetalert2';

import { Button, Label1 } from '@/pages/merchant/seat/lockerChange';

const buildCandidateName = (item, categoryMap) => {
  const parts = [`${item.number}번`];
  if (item.name) {
    parts.push(item.name);
  }
  const seatTypeLabel = item.seatType === 1 ? '고정석' : '자유석';
  parts.push(item.level ? `${item.level}등급 ${seatTypeLabel}` : seatTypeLabel);
  const categoryName = item.seatCategoryId ? categoryMap[item.seatCategoryId] : null;
  if (categoryName) {
    parts.push(categoryName);
  }
  if (item.floor) {
    parts.push(`${item.floor}층`);
  }
  return parts.join(' · ');
};

const ChangeModal = ({ isOpen, close, choiceModalClose, seat, setLists, lists }) => {
  const { createRequest } = useHandleAPICall();
  const { catchError } = useHandleError();
  const [changeNum, setChangeNum] = useState(0);
  const [categoryMap, setCategoryMap] = useState({});
  const [selectedCandidateId, setSelectedCandidateId] = useState(undefined);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const res = await createRequest(getSeatCategory);
        const map = {};
        (res?.data || []).forEach((c) => {
          map[c.id] = c.seatCategoryName;
        });
        setCategoryMap(map);
      } catch {
        // 카테고리 조회 실패는 치명 아님 — fallback 라벨로 표시
      }
    })();
  }, [isOpen]);

  // 입력 번호가 바뀌면 이전 선택 초기화
  useEffect(() => {
    setSelectedCandidateId(undefined);
  }, [changeNum]);

  const candidates = useMemo(() => {
    const n = +changeNum;
    if (!n) return [];
    return lists.filter((item) => item.number === n && item.seatType === seat.seatType);
  }, [changeNum, lists, seat.seatType]);

  const hasAnyNumberMatch = useMemo(
    () => lists.some((item) => item.number === +changeNum),
    [changeNum, lists],
  );

  const candidateOptions = useMemo(
    () => candidates.map((c) => ({ id: c.id, name: buildCandidateName(c, categoryMap) })),
    [candidates, categoryMap],
  );

  const getSeatListApi = async () => {
    try {
      const res = await createRequest(getSeatList, seat.merchantId);
      setLists(res.data.filter((item) => item.type === 'seat'));
    } catch (error) {
      catchError(error);
    }
  };

  const seatChange = async () => {
    if (!+changeNum) {
      Swal.fire({ icon: 'error', title: '좌석 번호를 입력해주세요.', timer: 3000 });
      return;
    }
    if (!hasAnyNumberMatch) {
      Swal.fire({
        icon: 'error',
        title: '해당번호는 없는 좌석번호입니다. 다시 입력해주세요.',
        timer: 3000,
      });
      return;
    }
    if (candidates.length === 0) {
      Swal.fire({
        icon: 'error',
        title: '선택하신 좌석으로 이동이 불가합니다.',
        text: `${seat.seatType === 1 ? '고정석' : '자유석'}만 이용가능`,
        timer: 3000,
      });
      return;
    }

    let target;
    if (candidates.length === 1) {
      target = candidates[0];
    } else if (!selectedCandidateId) {
      Swal.fire({
        icon: 'warning',
        title: '같은 번호의 좌석이 여러 개 있습니다.',
        text: '이동할 좌석을 선택해주세요.',
        timer: 3000,
      });
      return;
    } else {
      target = candidates.find((c) => c.id === selectedCandidateId);
      if (!target) return;
    }

    try {
      const data = await createRequest(patchBackofficeSeatMove, {
        ticketId: seat.ticketId,
        seatId: target.id,
      });

      if (data.status === 200) {
        Swal.fire({
          icon: 'success',
          title: '좌석변경이 완료됐습니다.',
          timer: 3000,
        }).then(() => {
          choiceModalClose();
          close();
          getSeatListApi();
        });
      }
    } catch (error) {
      // v2 좌석 이동 가드(LETMEUP-172) 실패는 서버가 response.data 문자열로
      // "이미 차단된 이용권입니다." / "이용 기간이 만료된 이용권입니다." 등 모달 메시지를 내려준다.
      // useHandleError 전역 변경(전 axios 호출에 영향)을 피하기 위해 좌석 이동 한정으로
      // 여기에서만 직접 추출. 그 외 에러는 기존 공용 catchError 로 위임.
      const serverMsg = typeof error?.response?.data === 'string'
        ? error.response.data.trim()
        : '';
      if (serverMsg) {
        Swal.fire({ icon: 'error', title: serverMsg, timer: 3000 });
      } else {
        catchError(error);
      }
    }
  };

  const showCandidatePicker = candidates.length > 1;

  return (
    <>
      <Modal centered isOpen={isOpen} toggle={close}>
        <ModalHeader className="modal-title" toggle={close}>
          좌석이동
        </ModalHeader>
        <ModalBody>
          <Label1>
            <h5>이동할 좌석번호를 입력해주세요.</h5>

            <Input
              type="text"
              value={changeNum}
              placeholder="좌석번호를 입력해주세요."
              onChange={({ target }) => setChangeNum(target.value)}
            />
          </Label1>
          {showCandidatePicker && (
            <div style={{ margin: '8px 0 12px' }}>
              <p style={{ margin: '0 0 6px', color: '#d97706', fontSize: 13 }}>
                같은 번호의 좌석이 {candidates.length}개 있습니다. 이동할 좌석을 선택해주세요.
              </p>
              <SearchableSelect
                options={candidateOptions}
                value={selectedCandidateId}
                onChange={setSelectedCandidateId}
                placeholder="이동할 좌석 선택"
              />
            </div>
          )}
          <div
            style={{
              display: 'flex',
              width: '100%',
              gap: '10px',
            }}>
            <Button color="#fff" onClick={close}>
              취소
            </Button>
            <Button onClick={seatChange} color={theme.primary}>
              좌석이동
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default ChangeModal;
